/**
 * The Expert — Marketer View destination (SYR-68, Phase E).
 *
 * Reuses the real AIExpertPage scoped to a single expert ("Head of Brand
 * Marketing", falling back to the first available), with the grid + page chrome
 * skipped via the `embedded`/`forcedExpertType` props. Brand comes from the
 * global MarketerScope. One source of truth — the full profile → streaming
 * chat → past conversations → credits/gating all come from the existing page.
 *
 * When the tier lacks AI Experts access the embedded page would still show the
 * profile (its lock only renders in non-embedded mode), so we gate here with a
 * locked teaser mirroring the dashboard's AI Experts upgrade treatment.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Tag } from 'antd';
import { LockOutlined, CrownOutlined, BulbOutlined, CloseOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import AIExpertPage from '../../../pages/AIExpertPage';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetBrandKnowledgeQuery,
  useGetBrandKnowledgeDocumentsQuery,
} from '../../../services/nestjs-base-api';
import { THEME } from '../config/theme';
import { useTierAccess } from '../../../hooks/useTierAccess';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { PERIOD_SUMMARY } from '../shell/ScopePill';
import type { ExpertChatScope } from '../../../hooks/useStreamingChat';

const EXPERT_NAME = 'Head of Brand Marketing';
const EXPERT_ROLE = 'Strategic Leadership & Creative Excellence';
const EXPERT_BLURB =
  'Your AI Head of Brand Marketing, a senior executive with experience leading brand teams at global corporations. Ask it anything about your equity data and get strategy, creative direction, and commercial recommendations grounded in your own numbers.';
const EXPERT_TAGS = [
  'Brand Strategy',
  'Campaign Management',
  'Team Leadership',
  'Go-to-Market',
  'Creative Direction',
];

// The seven Brand Knowledge profile fields; all blank (and no context
// documents) means the expert will answer without any brand grounding.
const KNOWLEDGE_FIELDS = [
  'positioning',
  'targetCustomers',
  'keyCompetitors',
  'toneOfVoice',
  'brandGoals',
  'productsServices',
  'additionalContext',
] as const;

export const ExpertPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const navigate = useScopedNavigate();
  const { activeBrand, scope, payload, cohorts, calculatedAt } = useMarketerScope();
  const { hasAiExpertsAccess, isLoading: tierLoading } = useTierAccess();
  const { user } = useAuth();

  // Brand Knowledge nudge: when nothing is filled in, answers are generic —
  // surface that above the chat rather than letting it fail silently.
  const knowledgeArgs = { jwtToken: user?.jwtToken || '', brandId: activeBrand?.id ?? 0 };
  const skipKnowledge = !user?.jwtToken || !activeBrand;
  const { data: knowledge } = useGetBrandKnowledgeQuery(knowledgeArgs, { skip: skipKnowledge });
  const { data: knowledgeDocs } = useGetBrandKnowledgeDocumentsQuery(knowledgeArgs, { skip: skipKnowledge });

  // Dismissal is per-brand and per-session: quiet within a visit, but the
  // nudge returns next session for as long as the knowledge stays empty.
  const dismissKey = activeBrand ? `brand-knowledge-nudge-dismissed-${activeBrand.id}` : '';
  const [nudgeDismissed, setNudgeDismissed] = useState(true);
  useEffect(() => {
    if (dismissKey) setNudgeDismissed(sessionStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);
  const dismissNudge = (): void => {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setNudgeDismissed(true);
  };

  // Hand the Data Selection pill's scope to the chat so the expert's numbers
  // come from the same window the dashboard shows, with a display label +
  // calculated-at stamp stored on the conversation.
  const chatScope = useMemo<ExpertChatScope | undefined>(() => {
    if (!payload) return undefined;
    const periodLabel = intl.formatMessage({
      id: PERIOD_SUMMARY[scope.periodType].id,
      defaultMessage: PERIOD_SUMMARY[scope.periodType].def,
    });
    const cohortLabel =
      scope.cohortIds.length === 0
        ? intl.formatMessage({ id: 'marketerView.scope.allCohorts', defaultMessage: 'All cohorts' })
        : cohorts
            .filter((c) => scope.cohortIds.includes(c.id))
            .map((c) => c.displayName || c.name)
            .join(', ') || `${scope.cohortIds.length}`;
    const countryLabel =
      scope.countryCodes.length === 0
        ? intl.formatMessage({ id: 'marketerView.scope.allCountries', defaultMessage: 'All countries' })
        : scope.countryCodes.join(', ');
    return {
      period: payload.period,
      dateFrom: payload.dateFrom,
      dateTo: payload.dateTo,
      cohortIds: payload.cohortIds,
      countryCodes: payload.countryCodes,
      label: `${periodLabel} \u00b7 ${cohortLabel} \u00b7 ${countryLabel}`,
      calculatedAt,
    };
  }, [payload, scope, cohorts, calculatedAt, intl]);

  const knowledgeEmpty =
    !!knowledge &&
    !!knowledgeDocs &&
    KNOWLEDGE_FIELDS.every((f) => !knowledge[f] || !knowledge[f]?.trim()) &&
    !knowledgeDocs.some((d) => d.includeInContext);
  const showNudge = knowledgeEmpty && !nudgeDismissed;

  if (!activeBrand) {
    return <MarketerLoading />;
  }

  // Locked tier: a single, centred "premium locked" hero for the one expert.
  if (!tierLoading && !hasAiExpertsAccess) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '24px 16px 48px',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 620,
            textAlign: 'center',
            background: '#fff',
            border: '1px solid #EEF1F5',
            borderRadius: 24,
            padding: '48px 48px 44px',
            boxShadow: '0 24px 60px -28px rgba(15,23,42,0.30)',
            overflow: 'hidden',
          }}
        >
          {/* Soft amber wash at the top for a premium feel */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 120,
              background:
                'linear-gradient(180deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0) 100%)',
              pointerEvents: 'none',
            }}
          />
          {/* Upgrade Required pill */}
          <div style={{ position: 'relative', marginBottom: 22 }}>
            <Tag
              color="default"
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '3px 12px',
                borderRadius: 999,
                background: 'rgba(245,158,11,0.12)',
                borderColor: 'rgba(245,158,11,0.35)',
                color: '#92630b',
              }}
            >
              <LockOutlined style={{ marginRight: 5 }} />
              {intl.formatMessage({ id: 'subscription.upgrade.required', defaultMessage: 'Upgrade Required' })}
            </Tag>
          </div>
          {/* Avatar with lock badge */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 18 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 92,
                height: 92,
                borderRadius: 999,
                background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                color: '#fff',
                boxShadow: '0 12px 28px -10px rgba(245,158,11,0.6)',
              }}
            >
              <CrownOutlined style={{ fontSize: 38 }} />
            </span>
            <span
              style={{
                position: 'absolute',
                right: -2,
                bottom: -2,
                width: 30,
                height: 30,
                borderRadius: 999,
                background: '#fff',
                border: '1px solid #EEF1F5',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                boxShadow: '0 4px 10px -4px rgba(15,23,42,0.3)',
              }}
            >
              <LockOutlined style={{ fontSize: 14 }} />
            </span>
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>
            {EXPERT_NAME}
          </h2>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#F59E0B', margin: '6px 0 16px' }}>
            {EXPERT_ROLE}
          </div>
          <p
            style={{
              fontSize: 15,
              color: '#64748B',
              lineHeight: 1.62,
              margin: '0 auto',
              maxWidth: 460,
            }}
          >
            {EXPERT_BLURB}
          </p>
          {/* Expertise chips */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 8,
              margin: '20px auto 28px',
              maxWidth: 480,
            }}
          >
            {EXPERT_TAGS.map((t) => (
              <span
                key={t}
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: '#475569',
                  background: '#F1F5F9',
                  border: '1px solid #E5E7EB',
                  borderRadius: 999,
                  padding: '4px 12px',
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<CrownOutlined />}
            onClick={() => navigate(MARKETER_UPGRADE_ROUTE)}
            style={{
              height: 46,
              padding: '0 28px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
              border: 'none',
              boxShadow: '0 12px 24px -10px rgba(245,158,11,0.6)',
            }}
          >
            {intl.formatMessage({
              id: 'subscription.upgrade.unlockExpert',
              defaultMessage: 'Upgrade to unlock The Expert',
            })}
          </Button>
          <p style={{ fontSize: 13, color: '#94A3B8', margin: '16px auto 0', maxWidth: 460 }}>
            {intl.formatMessage({
              id: 'subscription.upgrade.aiExpertsRequireUpgrade',
              defaultMessage:
                'Get expert AI insights about your brand strategy, consumer psychology, and analytics. Upgrade to unlock AI Experts.',
            })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showNudge && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '4px 16px 14px',
            padding: '12px 16px',
            background: THEME.primarySoft,
            border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 12,
          }}
        >
          <BulbOutlined style={{ color: THEME.primaryDark, fontSize: 18, flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.5, color: THEME.textBase }}>
            <FormattedMessage
              id="marketerView.expert.knowledgeNudge"
              defaultMessage="Want sharper answers? Fill in your Brand Knowledge and the {expert} will tailor its advice to your positioning, customers and goals."
              values={{ expert: EXPERT_NAME }}
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard/organisation?tab=brand-knowledge')}
            style={{
              border: 'none',
              background: THEME.primary,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              padding: '7px 14px',
              borderRadius: 8,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <FormattedMessage
              id="marketerView.expert.knowledgeNudgeCta"
              defaultMessage="Add Brand Knowledge"
            />
          </button>
          <button
            type="button"
            onClick={dismissNudge}
            aria-label={intl.formatMessage({
              id: 'marketerView.expert.knowledgeNudgeDismiss',
              defaultMessage: 'Dismiss',
            })}
            style={{
              border: 'none',
              background: 'transparent',
              color: THEME.textSecondary,
              cursor: 'pointer',
              padding: 4,
              flexShrink: 0,
              display: 'inline-flex',
            }}
          >
            <CloseOutlined style={{ fontSize: 12 }} />
          </button>
        </div>
      )}
      <AIExpertPage embedded forcedExpertType={EXPERT_NAME} brandId={activeBrand.id} scope={chatScope} />
    </>
  );
};
